import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, Flame, TrendingUp, AlertTriangle, Trophy, Copy, Loader2, FileText, CalendarClock,
} from "lucide-react";
import { copyText } from "@/lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, tone = "navy", testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="rounded-2xl bg-white border border-linen-border p-5 shadow-soft"
    data-testid={testid}
  >
    <span
      className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${
        tone === "lime" ? "bg-lime-soft text-navy" : tone === "warn" ? "bg-amber-100 text-amber-700" : "bg-teal-light text-teal"
      }`}
    >
      <Icon size={18} />
    </span>
    <div className="mt-3 text-3xl font-heading font-medium text-navy leading-none">{value}</div>
    <div className="mt-1 text-xs uppercase tracking-[0.16em] text-ink-500 font-semibold">{label}</div>
  </motion.div>
);

const StatusPill = ({ status }) => {
  const map = {
    approved: "bg-lime-soft text-navy",
    under_review: "bg-teal-light text-teal",
    new: "bg-linen text-ink-600",
    declined: "bg-red-50 text-red-700",
  };
  const label = { approved: "Approved", under_review: "In review", new: "Applied", declined: "Not a fit", not_approved: "Not approved" }[status] || status;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${map[status] || "bg-linen text-ink-600"}`}>
      {label}
    </span>
  );
};

export default function RosterDashboard() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get("k") || "", []);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) {
      setErr("This page needs your private roster link. Check the email we sent you.");
      return;
    }
    axios
      .get(`${API}/brokerage/roster`, { params: { k: token } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "We couldn't load your roster. Ask us for a fresh link."));
  }, [token]);

  if (err) {
    return (
      <main className="min-h-screen bg-linen flex items-center justify-center px-5">
        <div className="max-w-md text-center" data-testid="roster-error">
          <h1 className="font-heading font-medium text-3xl text-navy">Roster unavailable</h1>
          <p className="mt-3 text-ink-600 leading-relaxed">{err}</p>
          <a href="mailto:daren@greatratecheck.com" className="mt-6 inline-flex rounded-full bg-navy text-white font-semibold text-sm px-6 py-3">
            Email us for a new link
          </a>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="min-h-screen bg-linen flex items-center justify-center">
        <Loader2 className="animate-spin text-teal" size={28} />
      </main>
    );
  }

  const { brokerage, stats, agents, invite_code } = data;
  const inviteUrl = `${window.location.origin}/partnerapply?b=${invite_code}`;

  return (
    <main className="min-h-screen bg-linen" data-testid="roster-dashboard">
      <section className="bg-navy text-white">
        <div className="container-grc py-12 sm:py-16">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-lime">Roster view</div>
          <h1 className="mt-3 font-heading font-medium text-3xl sm:text-4xl lg:text-5xl text-balance">
            {brokerage.name}
          </h1>
          <p className="mt-4 text-white/70 leading-relaxed max-w-2xl">
            {brokerage.leader_name ? `${brokerage.leader_name.split(" ")[0]} — this is` : "This is"} where your agents
            stand this month: who's working the program, who's gone quiet, and what's come through their links.
          </p>
          <div className="mt-6 inline-flex flex-wrap items-center gap-3 rounded-2xl bg-white/[0.07] border border-white/15 px-4 py-3">
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-lime">Invite your agents</span>
            <code className="text-sm text-white/90" data-testid="roster-invite-url">{inviteUrl}</code>
            <button
              onClick={async () => {
                const ok = await copyText(inviteUrl);
                toast[ok ? "success" : "error"](ok ? "Invite link copied" : "Copy it manually");
              }}
              data-testid="roster-invite-copy"
              className="inline-flex items-center gap-1.5 rounded-full bg-lime text-navy-deep font-semibold text-xs px-3 py-1.5 hover:bg-lime-hover transition-colors"
            >
              <Copy size={13} /> Copy
            </button>
          </div>
        </div>
      </section>

      <section className="container-grc py-10 sm:py-14 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Users} label="Agents on roster" value={stats.agents} testid="roster-stat-agents" />
          <Stat icon={TrendingUp} label="Active this month" value={stats.active_this_month} tone="lime" testid="roster-stat-active" />
          <Stat icon={AlertTriangle} label="Stalled" value={stats.stalled} tone="warn" testid="roster-stat-stalled" />
          <Stat icon={FileText} label="Applications started" value={stats.apps_started} testid="roster-stat-apps" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={CalendarClock} label="Approved" value={stats.approved} testid="roster-stat-approved" />
          <Stat icon={CalendarClock} label="Waiting on approval" value={stats.pending} testid="roster-stat-pending" />
          <Stat icon={Trophy} label="First wins logged" value={stats.first_wins} tone="lime" testid="roster-stat-wins" />
          <Stat icon={Flame} label="Longest streak" value={`${stats.best_streak} wk`} testid="roster-stat-streak" />
        </div>

        {data.annual ? (
          <div
            className="rounded-3xl bg-navy text-white px-6 py-5 flex flex-wrap items-center justify-between gap-4"
            data-testid="roster-annual-strip"
          >
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-lime">
                {data.annual.label}
              </div>
              <p className="mt-1.5 text-sm text-white/75 max-w-xl leading-relaxed">
                {data.annual.open
                  ? `Season is open and closes ${data.annual.closes_on} — ${data.annual.days_left} days left.`
                  : data.annual.phase === "before" && data.annual.enabled
                    ? `Season opens ${data.annual.opens_on}.`
                    : "Season is closed — agents keep running anniversary checks year-round."}{" "}
                {data.annual.agents_running} of your agents have started theirs.
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-heading font-medium leading-none">
                {data.annual.checked}
                <span className="text-white/40 text-xl">/{data.annual.contacts}</span>
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-white/55 font-semibold">
                Homeowners checked
              </div>
            </div>
          </div>
        ) : null}

        <div className="rounded-3xl bg-white border border-linen-border shadow-soft overflow-hidden">
          <div className="px-6 py-5 border-b border-linen-border flex items-center justify-between gap-4">
            <div>
              <h2 className="font-heading font-medium text-xl text-navy">Your agents</h2>
              <p className="text-xs text-ink-500 mt-0.5">Month of {data.month}. Most active first.</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="roster-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-ink-500 bg-linen/60">
                  <th className="px-6 py-3 font-semibold">Agent</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">This month</th>
                  <th className="px-4 py-3 font-semibold">Streak</th>
                  <th className="px-4 py-3 font-semibold">Annual check</th>
                  <th className="px-4 py-3 font-semibold">Apps started</th>
                  <th className="px-4 py-3 font-semibold">Submitted</th>
                  <th className="px-6 py-3 font-semibold">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr key={a.email} className="border-t border-linen-border" data-testid={`roster-row-${a.email}`}>
                    <td className="px-6 py-3">
                      <div className="font-semibold text-navy">{a.name}</div>
                      <div className="text-[11px] text-ink-500">{a.city_state || a.email}</div>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={a.status} /></td>
                    <td className="px-4 py-3">
                      {a.active_this_month ? (
                        <span className="text-teal font-semibold">{a.activity_this_month} logged</span>
                      ) : a.stalled ? (
                        <span className="text-amber-700 font-semibold">Stalled</span>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {a.streak > 0 ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-navy">
                          <Flame size={13} className="text-lime" /> {a.streak}
                        </span>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {a.annual_total > 0 ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="w-16 h-1.5 rounded-full bg-linen overflow-hidden">
                            <span className="block h-full rounded-full bg-teal" style={{ width: `${a.annual_pct}%` }} />
                          </span>
                          <span className="text-[11px] font-semibold text-navy">
                            {a.annual_checked}/{a.annual_total}
                          </span>
                        </span>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-navy font-semibold">{a.apps_started || "—"}</td>
                    <td className="px-4 py-3 text-navy font-semibold">{a.apps_submitted || "—"}</td>
                    <td className="px-6 py-3 text-ink-500 text-[12px]">{a.last_activity_on || "No activity yet"}</td>
                  </tr>
                ))}
                {agents.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-10 text-center text-ink-500" data-testid="roster-empty">
                      No agents yet. Share your invite link above — everyone who applies through it lands here.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-ink-500 leading-relaxed max-w-3xl">
          Activity reflects what agents log in their partner workspace. Applications started are consumer
          applications begun through an agent's branded link. All mortgage origination is performed by
          licensed professionals; participation and any compensation are subject to program approval and
          applicable regulations, including RESPA.
        </p>
      </section>
    </main>
  );
}
